import { GetCandidateInterviewByCPSOutput } from '$store/modules/interview';
import PositionDto from '$types/serverTypes/positionDto';
import { FileMediaType } from '$types/fileMediaType';
import CompanyDto from './serverTypes/CompanyDto';

export interface CandidatesApiData {
  data: CandidateData[];
  meta: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    itemCount: number;
    page: number;
    pageCount: number;
    take: number;
  };
}

export interface Schedule {
  Date: string;
  Id?: number;
  Timezone?: string;
}

export interface CandidateData {
  Id: number;
  Score: number;
  Videos?: CandidateDataVideo[] | null;
  CreateTime: Date;
  User?: {
    FirstName: string;
    LastName: string;
    ProfilePicture?: {
      Url?: string;
      Key: string;
    } | null;
    Email: string;
  };
  Tenant?: {
    Id: number;
    Name: string;
  };
  Files?: {
    Description: string;
    Url: string;
    FileCategory: {
      CategoryName: string;
      Id: number;
    } | null;
    Id: number;
    MediaType?: FileMediaType;
    IsEmployersVisible?: boolean;
  }[];
  WorkSummary?: string | null;
  CandidateSkills?:
    | {
        Id: number;
        Skill: {
          Title: string;
        };
        IsVerified: boolean;
      }[]
    | null;
  CandidateEducation?: {
    DegreeName: string;
    DegreeType: string;
    Id: number;
  }[];
  CandidateJobInterest?: {
    Id: number;
    SalaryLowerBound: number;
    SalaryUpperBound: number;
  };
  CandidateLanguages?:
    | {
        Id: number;
        LanguageName: string;
      }[]
    | [];
  AdditionalTags?: {
    Name: string;
    Id: number;
  }[];
  CandidatesPositionStatus?: {
    Id: number;
    CreateTime: Date;
    Status: string;
    Highlight: boolean;
  }[];
  CandidateWorkExperience?: {
    Admission: string;
    Company: string;
    JobTitle: string;
    Egress: string;
    Id: number;
  }[];
  YearsOfExperience?: number;
  Curriculum: {
    Id: number;
    Url: string;
  } | null;
  CustomUrl: string | null;
  GithubUrl: string | null;
  BehanceUrl: string | null;
  ScorecardUrl: string | null;
  AdditionalVideos: CandidateAdditionalVideoType[];
  CandidateInterviewStatus: GetCandidateInterviewByCPSOutput | undefined;
  ResidenceCountry?: string;
  IsSimeraAICertified: boolean;
  SmartInterviewsVideos?: CandidateSmartInterviewsVideos[];
  CandidateReferrals?: CandidateReferralData[];
}

export interface CandidateReferralData {
  Id: number;
  Status: string;
  ReferralName?: string | null;
  ReferralEmail: string;
  ReferralRelationship?: string | null;
  PositionHeldByCandidate?: string | null;
  WouldHireAgain?: boolean | null;
  LikelihoodToRecommend?: number | null;
  CandidateReliability?: string | null;
  CandidateCollaboration?: string | null;
  CandidateWorkEthic?: string | null;
  CandidateResponsibilities?: string | null;
  CandidatePerformance?: string | null;
  CreateTime?: string | null;
}

export interface CandidateSmartInterviewsVideos {
  Id: number;
  Transcription?: Record<string, unknown> | null;
  SmartInterviewQuestions: {
    Id: number;
    Name: string;
  } | null;
  Questions: string[];
  Metadata: {
    welcome: string;
  };
  Status: string;
  StartTime: string;
  EndTime: string | null;
  RawUrl: string;
  RawKey: string;
  Key: string;
  Url: string;
  UpdateTime: string;
}

export interface CandidateDataVideo {
  EnglishAssessment: {
    Score: string;
  } | null;
  Url: string;
  Id: number;
  Type: number;
  Key: string;
}

export interface CandidateAdditionalVideoType {
  Id: number;
  Name: string;
  Key: string;
  Url: string;
  ShareReferenceId: string;
}

export interface Candidate {
  candidate_Id?: number;
  candidate_CreateTime?: Date;
  candidate_Score?: number;
  candidate_WhatsAppNumber?: string;
  user_FirstName?: string;
  user_LastName?: string;
  user_Email?: string;
  user_Phone?: string;
  cps_Status?: null | string;
  tenant_Name?: string;
  positioncount?: null | string;
  Id: number;
  Pending: SaveState;
  CreateTime: Date;
  Completed?: boolean;
  PositionCount: number;
  WorkSummary?: string;
  User?: {
    FullName?: string;
    Url?: string;
    FirstName: string;
    LastName: string;
    Email?: string;
    Id?: number;
    ProfilePicture?: {
      Key: string;
      Url: string;
    } | null;
    Country?: string;
    Phone?: string;
    AddresLine1?: string;
    AddresLine2?: string;
  };
  Score: number;
  CandidateJobInterest?: {
    Id: number;
    SalaryLowerBound: number;
    SalaryUpperBound: number;
    JobInterest: { title: string }[];
  };
  CandidateSkills: {
    CertificationDate?: string;
    CertificationId?: string;
    CertificationUrl?: string;
    Id: number;
    Skill: { title: string };
    SkillLevel?: string;
  }[];
  CandidateWorkExperience: {
    Id: number;
    Company: string;
    Admission: string;
    Egress?: null | string | undefined;
    JobPosition: AutocompleteOption;
    JobTitle?: string;
    Country?: string | null | undefined;
    Description?: string | null | undefined;
  }[];
  CandidateLanguages: { LanguageName: string; LanguageProficiency: string }[];
  CandidateEducation?: {
    Id: number;
    CurrentlyEnrolled: boolean;
    DegreeName: string;
    DegreeType: DegreeTypes;
    EducationalInstitutionName: string;
    FinishDate: string;
  }[];
  CandidateTechnicalInformation?: {
    Id: number;
    CPUModel: string;
    RAM: number;
    Storage: string;
    OperatingSystem: string;
    AvailableSpace: string;
  };
  CandidatesPositionStatus?: {
    Id: number;
    Status: SwitchTypes;
    CreateTime: Date;
    Position: {
      Id: number;
      Status: PositionStatus;
      Company: CompanyDto;
    };
  }[];
  CandidateExtracurriculars?: {
    Id: number;
    ExtracurricularType: string;
    ExtracurricularDetail: string;
  }[];
  videos: CandidateVideos[];
  CustomUrl: string | null;
  GithubUrl: string | null;
  LinkedinUrl: string | null;
  WhatsAppNumber?: string | null;
  BehanceUrl: string | null;
  Curriculum?: CurriculumDto | null;
  YearsOfExperience?: number;
  ResidenceCountry?: string;
  AddresLine1?: string;
  AddresLine2?: string;
  ScorecardUrl?: string;
  DoNotHire: boolean | null;
  QualityStatus: string | null;
  OpenToWork: string | null;
  IsSimeraAICertified: boolean;
}

export interface JobPosition {
  Id: number;
  title: string;
}
export interface AdditionalTag {
  Id: number;
  title: string;
}
export interface AdditionalTagResponse {
  Id: number;
  Name: string;
  Deprecated: boolean;
}

export interface AdditionalDocument {
  Id: number;
  Key: string;
  Url: string;
  Description: string;
  FileCategory: {
    Id: number;
  } | null;
  IsCandidatesVisible: boolean;
  IsEmployersVisible: boolean;
  MediaType?: FileMediaType;
}

export interface Skill {
  Id: number;
  title: string;
}

export interface AutocompleteOption {
  Id: number;
  title: string;
}

export interface Crumbs {
  id?: number;
  path?: string;
  name: string;
}
export interface CandidateStatusQuery {
  PositionId: number | string;
  Status?: ActiveValues | null;
}
export interface FeedbackFormValues {
  FeedbackType: FeedbackType;
  Feedback: string;
}

export interface FilterPositionQuery {
  companies?: number[];
  term: FilterType;
  jobPositions?: number[];
  jobIds?: number[];
}

export interface Referral {
  Id: number;
  RefereeAction: string;
  CreateTime: string;
  UpdateTime: string;
  Referree: {
    Id: number;
    FirstName: string;
    LastName: string;
    Email: string;
  };
}

export interface FileCategory {
  CategoryName: string;
  CreateTime: string;
  DeleteTime: string;
  Deprecated: boolean;
  Id: number;
  UpdateTime: string;
}

export interface PaginatedEmployers {
  data: EmployerData[];
  meta: Meta;
}

export interface EmployerData {
  AgreedTermsOfServiceDate: boolean | null;
  Company: Company;
  CreateTime: Date;
  DeleteTime?: Date | null;
  Id: number;
  UpdateTime: Date;
  User: User;
}
export interface Company {
  CreateTime: Date;
  DeleteTime: Date | null;
  Domain: string;
  Id: number;
  Name: string;
  UpdateTime: Date;
}

export interface User {
  ConfirmToken: string;
  CreateTime: Date;
  DeleteTime?: Date;
  Email: string;
  EmailVerified: boolean;
  FirstName: string;
  Id: number;
  LastLogin: Date;
  LastName: string;
  Phone: string | null;
  SignUpType: number;
  Unsubscribed: boolean;
  UpdateTime: Date;
}

export interface CompaniesData {
  data: CompaniesTable[];
  meta: Meta;
}

export interface CompaniesTable {
  companyId: number;
  companyName: string;
  openPositions: string;
  totalPositions: string;
  totalHired: string;
  formerEmployeesTotal: string;
  companyUrl: string;
  companyLogo: string;
}

export interface PositionData {
  data: PositionDto[];
  meta: {
    hasPreviousPage?: boolean;
    hasNextPage: boolean;
    pageCount?: number;
    page?: number;
    itemCount?: number;
  };
}

export interface EmployeesData {
  Id: number;
  acceptedTime: string;
  Salary?: number;
  EndDate?: string;
  StartDate?: string;
  StartDate?: Date;
  Salary: number;
  EndDate: Date;
  Benefits?: number;
  SimeraFee?: number;
  EndReason?: EndReason;
  AdditionalNotesEndDate?: string;
  Position: PositionEmployee;
  Candidate: CandidateEmployee;
}

export interface RosterData {
  id: number;
  name: string;
  activerosters: string;
  activeinterviews: string;
}

export interface CandidatesSearchState {
  query: {
    JobTitle: (AutocompleteOption | undefined)[];
    Education?: DegreeTypes[];
    Skills: (AutocompleteOption | undefined)[];
    Salary?: number[];
    Language: (string | undefined)[];
    Extracurricular: (string | undefined)[];
    Location: (string | undefined)[];
    Company: (AutocompleteOption | undefined)[];
  };
}

export interface Note {
  Id: number;
  Title: string;
  Content: string;
  CreatedBy: CreatedBy;
  CreateTime: string;
  Position: PositionNote | null;
}

export interface CandidatesData {
  data: Candidate[];
  meta: {
    hasPreviousPage?: boolean;
    hasNextPage: boolean;
    pageCount?: number;
    page?: number;
  };
}

export interface CandidatesPaginationQuery {
  order: Order;
  page: number;
  take: number;
  Id?: number;
  Name?: string;
  Phone?: string;
  Email?: string;
  StartDate?: string;
  EndDate?: string;
  TenantIds?: number[];
}

export interface TaskData {
  data: Task[];
  meta: meta;
}

export interface Task {
  Id: number;
  CreateTime: string;
  UpdateTime: string;
  DeleteTime: string | null;
  Status: number;
  Percentage: number;
  Log: string | null;
  User: User;
}

export type Auth0ResultExt = Auth0Result & {
  idTokenPayload?: {
    email: string;
    emailVerified: boolean;
    email_verified: boolean;
    exp: number;
    familyName: string;
    family_name: string;
    givenName: string;
    given_name: string;
    iat: number;
    iss: string;
    locale: string;
    name: string;
    nickname: string;
    nonce: string;
    picture: string;
  };
};

export type IAuth0Response = Auth0Result & {
  Id?: string;
};

interface Language {
  name: string;
  nativeName: string;
}

interface ICandidateVideos {
  url?: string;
  type: number;
  key?: string;
}

interface FormSteps {
  title: string;
  highlight: string;
  subtitle: string;
  children: React.FC | FC<any>;
  icon: ReactComponentElement;
  type: string;
  totalSteps?: number;
  completedSteps?: number;
}

export type CandidatePositionDataOnGuestMode = {
  Id: number;
  Skills_position: {
    Id: number;
    Title: string;
  }[];
  JobPosition: {
    Id: number;
    Title: string;
  };
  CandidatesPositionStatus: CandidatesPositionStatusOnGuestMode[];
};

export type CandidatesPositionStatusOnGuestMode = {
  Id: number;
  User: {
    Id: number;
    FirstName: string;
    LastName: string;
    ProfilePicture: { Url: string } | null;
  };
  CandidateSkills: { Id: number; Skill: { Id: number; Title: string } }[];
  CandidateWorkExperience: { Id: number; Admision: string; Egress: string }[];
  AdditionalVideos: { Id: number; Url: string; Key: string; Name: string }[];
  YearsOfExperience: number;
  CandidateJobInterest: {
    Id: number;
    SalaryLowerBound: number;
    SalaryUpperBound: number;
  };
};

export enum QualityStatusTypes {
  '5-Star' = '5-Star',
  Good = 'Good',
  Farm = 'Farm',
  Bad = 'Bad',
}

export enum OpenToWorkTypes {
  Active = 'Active',
  Interested = 'Interested',
  'Not Available' = 'Not Available',
}

export interface CandidateAssessmentAnswer {
  Id: number;
  Situation: string;
  Task: string;
  Answer: string;
  SortOrder: number;
}

export interface CandidateAssessmentDetail {
  Id: number;
  Title: string;
  Assessments: CandidateAssessmentAnswer[];
}
